import React, { useMemo } from 'react';
import { GlobeIcon } from 'lucide-react';
import { Select } from '../ui/Field';
import { Badge } from '../ui/Badge';
import { useGovernance } from '../../contexts/GovernanceContext';
import { scopeCovers } from '../../utils/governance';
import type { Scope } from '../../types/governance';
interface Option {
  id: string;
  label: string;
  scope: Scope;
  depth: number;
}

/**
 * The console's context selector. Changing it changes which records are
 * reachable — the surfaces read `activeScope`, so nothing from the previous
 * context can survive the switch.
 *
 * Only scopes the signed-in administrator actually holds are offered.
 */
export function ScopeSwitcher() {
  const {
    tenants,
    me,
    activeScope,
    setActiveScope,
    scopeLabel
  } = useGovernance();
  const options = useMemo<Option[]>(() => {
    const held = me.assignments.filter(assignment => assignment.state === 'active').map(assignment => assignment.scope);
    const reachable = (scope: Scope) => held.some(holder => scopeCovers(holder, scope));
    const list: Option[] = [];
    const globalScope: Scope = {
      level: 'global'
    };
    if (reachable(globalScope)) {
      list.push({
        id: 'global',
        label: 'Campuzon global',
        scope: globalScope,
        depth: 0
      });
    }
    for (const tenant of tenants) {
      const tenantScope: Scope = {
        level: 'tenant',
        tenantId: tenant.id
      };
      if (reachable(tenantScope)) {
        list.push({
          id: tenant.id,
          label: tenant.name,
          scope: tenantScope,
          depth: 1
        });
      }
      for (const institution of tenant.institutions) {
        for (const campus of institution.campuses) {
          const campusScope: Scope = {
            level: 'campus',
            tenantId: tenant.id,
            institutionId: institution.id,
            campusId: campus.id
          };
          if (reachable(campusScope)) {
            list.push({
              id: campus.id,
              label: `${institution.name} — ${campus.name}`,
              scope: campusScope,
              depth: 2
            });
          }
        }
      }
    }
    return list;
  }, [me.assignments, tenants]);
  const activeId = activeScope.level === 'global' ? 'global' : activeScope.campusId ?? activeScope.tenantId ?? '';
  return <div className="flex flex-wrap items-end gap-3" data-id="element-398" data-mp="q0862cg0okv1">
      <div className="min-w-[260px]" data-id="element-399" data-mp="u11potmffdi4">
        <label htmlFor="scope-switch" className="mb-1.5 block text-body-sm font-medium text-ink" data-id="element-400" data-mp="u007m56ztqqw">
          Operating context
        </label>
        <Select id="scope-switch" value={activeId} onChange={event => {
        const chosen = options.find(option => option.id === event.target.value);
        if (chosen) setActiveScope(chosen.scope);
      }} data-id="element-401" data-mp="2a41a1r3ef2g">
          {options.map(option => <option key={option.id} value={option.id} data-id="element-402" data-mp="1hqw1d7v0ew0s">
              {`${'\u00A0'.repeat(option.depth * 3)}${option.label}`}
            </option>)}
        </Select>
      </div>
      <Badge tone="brand" icon={<GlobeIcon className="h-4 w-4" data-id="element-404" data-mp="1wufdaw6hr90f" />} data-id="element-403" data-mp="1mrs8rvekl0ay">
        {scopeLabel(activeScope)}
      </Badge>
    </div>;
}